import React, { useEffect, useRef } from 'react';

/**
 * WaterGridBackground
 * Interactive fluid wave & water ripple canvas grid.
 * Bends and undulates grid lines smoothly under mouse movement and clicks.
 */
const WaterGridBackground = ({
  gridSize = 30,
  lineColor = 'rgba(24, 24, 27, 0.085)',
  maskCenter = '50% 45%'
}) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const parent = canvas.parentElement;
    if (!parent) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animId = null;
    let width = 0;
    let height = 0;
    let dpr = window.devicePixelRatio || 1;

    // Mouse tracking
    const mouse = { x: -9999, y: -9999, active: false, targetX: -9999, targetY: -9999 };
    const lastEmit = { x: -9999, y: -9999 };
    const ripples = [];
    let isRunning = false;
    let idleFrames = 0;

    function wake() {
      if (!isRunning) {
        isRunning = true;
        idleFrames = 0;
        animId = requestAnimationFrame(draw);
      }
    }

    function addRipple(x, y, strength = 10, maxRadius = 160, speed = 3.2) {
      ripples.push({
        x,
        y,
        radius: 0,
        maxRadius,
        speed,
        strength,
        life: 1
      });
      if (ripples.length > 25) ripples.shift();
      wake();
    }

    function resize() {
      const rect = parent.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      dpr = window.devicePixelRatio || 1;

      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);

      wake();
    }

    function handleMouseMove(e) {
      const rect = parent.getBoundingClientRect();
      mouse.targetX = e.clientX - rect.left;
      mouse.targetY = e.clientY - rect.top;
      mouse.active = true;

      const dist = Math.hypot(mouse.targetX - lastEmit.x, mouse.targetY - lastEmit.y);
      if (dist > 22) {
        addRipple(mouse.targetX, mouse.targetY, 7, 130, 3.0);
        lastEmit.x = mouse.targetX;
        lastEmit.y = mouse.targetY;
      }
      wake();
    }

    function handleMouseLeave() {
      mouse.active = false;
      wake();
    }

    function handlePointerDown(e) {
      const rect = parent.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      addRipple(x, y, 16, 260, 4.2);
    }

    function draw() {
      if (mouse.active) {
        mouse.x += (mouse.targetX - mouse.x) * 0.25;
        mouse.y += (mouse.targetY - mouse.y) * 0.25;
      } else {
        mouse.x = -9999;
        mouse.y = -9999;
      }

      ctx.clearRect(0, 0, width, height);

      const step = gridSize;
      const cols = Math.ceil(width / step) + 1;
      const rows = Math.ceil(height / step) + 1;

      let hasActiveRipples = false;
      for (let r = ripples.length - 1; r >= 0; r--) {
        const rip = ripples[r];
        rip.radius += rip.speed;
        rip.life = Math.max(0, 1 - (rip.radius / rip.maxRadius));
        if (rip.radius >= rip.maxRadius || rip.life <= 0) {
          ripples.splice(r, 1);
        } else {
          hasActiveRipples = true;
        }
      }

      const points = new Array(rows + 1);
      const isMouseActive = mouse.active && mouse.x > -100 && mouse.x < width + 100 && mouse.y > -100 && mouse.y < height + 100;

      for (let j = 0; j <= rows; j++) {
        const row = new Array(cols + 1);
        const py = j * step;

        for (let i = 0; i <= cols; i++) {
          const px = i * step;
          let ox = 0;
          let oy = 0;

          if (isMouseActive) {
            const d = Math.hypot(px - mouse.x, py - mouse.y);
            if (d < 110) {
              const f = Math.sin((d / 110) * Math.PI) * (1 - d / 110) * 8;
              const angle = Math.atan2(py - mouse.y, px - mouse.x);
              ox += Math.cos(angle) * f;
              oy += Math.sin(angle) * f;
            }
          }

          for (let r = 0; r < ripples.length; r++) {
            const rip = ripples[r];
            const d = Math.hypot(px - rip.x, py - rip.y);
            const diff = d - rip.radius;
            if (Math.abs(diff) < 36) {
              const factor = Math.sin((diff / 36) * Math.PI) * rip.strength * rip.life;
              const angle = Math.atan2(py - rip.y, px - rip.x);
              ox += Math.cos(angle) * factor;
              oy += Math.sin(angle) * factor;
            }
          }

          row[i] = { x: px + ox, y: py + oy };
        }
        points[j] = row;
      }

      ctx.beginPath();
      for (let j = 0; j <= rows; j++) {
        const row = points[j];
        ctx.moveTo(row[0].x, row[0].y);
        for (let i = 1; i <= cols; i++) {
          ctx.lineTo(row[i].x, row[i].y);
        }
      }

      for (let i = 0; i <= cols; i++) {
        ctx.moveTo(points[0][i].x, points[0][i].y);
        for (let j = 1; j <= rows; j++) {
          ctx.lineTo(points[j][i].x, points[j][i].y);
        }
      }

      ctx.strokeStyle = lineColor;
      ctx.lineWidth = 1;
      ctx.stroke();

      if (!isMouseActive && !hasActiveRipples) {
        idleFrames++;
        if (idleFrames > 30) {
          isRunning = false;
          animId = null;
          return;
        }
      } else {
        idleFrames = 0;
      }

      animId = requestAnimationFrame(draw);
    }

    parent.addEventListener('mousemove', handleMouseMove, { passive: true });
    parent.addEventListener('mouseleave', handleMouseLeave, { passive: true });
    parent.addEventListener('pointerdown', handlePointerDown, { passive: true });

    const ro = new ResizeObserver(() => resize());
    ro.observe(parent);
    resize();

    return () => {
      if (animId) cancelAnimationFrame(animId);
      ro.disconnect();
      parent.removeEventListener('mousemove', handleMouseMove);
      parent.removeEventListener('mouseleave', handleMouseLeave);
      parent.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [gridSize, lineColor, maskCenter]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 0,
        maskImage: `radial-gradient(ellipse 75% 65% at ${maskCenter}, rgba(0, 0, 0, 1) 20%, rgba(0, 0, 0, 0.25) 70%, transparent 100%)`,
        WebkitMaskImage: `radial-gradient(ellipse 75% 65% at ${maskCenter}, rgba(0, 0, 0, 1) 20%, rgba(0, 0, 0, 0.25) 70%, transparent 100%)`
      }}
    />
  );
};

export default React.memo(WaterGridBackground);
